import React, { useState, useEffect } from 'react';
import { X, Clock, AlertTriangle } from 'lucide-react';

const DAYS_OF_WEEK = [
  { value: 1, label: 'Sunday' },
  { value: 2, label: 'Monday' },
  { value: 3, label: 'Tuesday' },
  { value: 4, label: 'Wednesday' },
  { value: 5, label: 'Thursday' },
  { value: 6, label: 'Friday' },
  { value: 7, label: 'Saturday' },
];

interface Props {
  context: {
    checkpoint_id: number;
    checkpoint_name: string;
    day_of_week: number;
    lanetype_id: number;
  };
  availableLaneTypes: any[];
  existingHours: any[];
  onSave: (data: any) => void;
  onCancel: () => void;
  saving: boolean;
}

export const LaneOperatingHoursEditor: React.FC<Props> = ({
  context,
  availableLaneTypes,
  existingHours,
  onSave,
  onCancel,
  saving
}) => {
  // Check if day is pre-selected (from cell click) or needs to be chosen (from empty state button)
  const isDayPreSelected = context.day_of_week > 0;
  const isLaneTypePreSelected = context.lanetype_id > 0;
  
  // Auto-select the lane type from context if provided
  const [selectedLaneTypeIds, setSelectedLaneTypeIds] = useState<number[]>(
    isLaneTypePreSelected ? [context.lanetype_id] : []
  );
  
  // Day selection - use context day if provided, otherwise default to Monday
  const [selectedDay, setSelectedDay] = useState<number>(
    isDayPreSelected ? context.day_of_week : 2
  );
  
  // Time settings
  const [startTime, setStartTime] = useState<string>('06:00');
  const [endTime, setEndTime] = useState<string>('10:00');

  // Overlap detection
  const [overlappingSlots, setOverlappingSlots] = useState<Set<string>>(new Set());

  const dayLabel = DAYS_OF_WEEK.find(d => d.value === selectedDay)?.label || 'Unknown';

  // Filter lane types for this checkpoint
  const checkpointLaneTypes = availableLaneTypes.filter(
    lt => lt.checkpoint_id === context.checkpoint_id
  );

  // Check for overlaps whenever time, day, or selected lane types change
  useEffect(() => {
    checkOverlaps();
  }, [startTime, endTime, selectedLaneTypeIds, selectedDay]);

  const checkOverlaps = () => {
    const newOverlaps = new Set<string>();

    if (!startTime || !endTime || startTime >= endTime) {
      setOverlappingSlots(newOverlaps);
      return;
    }

    const newStart = startTime;
    const newEnd = endTime;

    selectedLaneTypeIds.forEach(laneTypeId => {
      const existingForLaneType = existingHours.filter(h => 
        h.lanetype.lanetype_id === laneTypeId && 
        h.day_of_week === selectedDay
      );

      existingForLaneType.forEach(existing => {
        const existingStart = existing.start_time.substring(0, 5);
        const existingEnd = existing.end_time.substring(0, 5);

        // Check if time ranges overlap
        if (
          (newStart >= existingStart && newStart < existingEnd) ||
          (newEnd > existingStart && newEnd <= existingEnd) ||
          (newStart <= existingStart && newEnd >= existingEnd)
        ) {
          newOverlaps.add(`${laneTypeId}-${existing.lane_operating_hours_id}`);
        }
      });
    });

    setOverlappingSlots(newOverlaps);
  };

  // Get existing slots for a lane type
  const getExistingSlotsForLaneType = (laneTypeId: number) => {
    return existingHours
      .filter(h => 
        h.lanetype.lanetype_id === laneTypeId && 
        h.day_of_week === selectedDay
      )
      .map(h => ({
        id: h.lane_operating_hours_id,
        start: h.start_time.substring(0, 5),
        end: h.end_time.substring(0, 5)
      }));
  };

  const isSlotOverlapping = (laneTypeId: number, slotId: number) => {
    return overlappingSlots.has(`${laneTypeId}-${slotId}`);
  };

  const toggleLaneType = (laneTypeId: number) => {
    setSelectedLaneTypeIds(prev => 
      prev.includes(laneTypeId)
        ? prev.filter(id => id !== laneTypeId)
        : [...prev, laneTypeId]
    );
  };

  const hasOverlaps = overlappingSlots.size > 0;
  const isValidTimeRange = startTime && endTime && startTime < endTime;
  const canSubmit = selectedLaneTypeIds.length > 0 && isValidTimeRange && !hasOverlaps;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!canSubmit) return;

    // Bulk create
    onSave({
      lanetype_ids: selectedLaneTypeIds,
      day_of_week: selectedDay,
      start_time: startTime + ':00',
      end_time: endTime + ':00',
      weekday: dayLabel
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-700">
          <h2 className="text-xl font-bold text-white">
            Add Operating Hours
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Context Display */}
          <div className="bg-gray-750 rounded-lg p-4">
            <div className="text-sm text-gray-400 mb-1">Adding hours for:</div>
            <div className="text-lg font-semibold text-white">
              {context.checkpoint_name}
              {isDayPreSelected && ` - ${dayLabel}`}
            </div>
          </div>

          {/* Day Selection - Only show if day is not pre-selected */}
          {!isDayPreSelected && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Select Day *
              </label>
              <select
                value={selectedDay}
                onChange={(e) => setSelectedDay(Number(e.target.value))}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                {DAYS_OF_WEEK.map(day => (
                  <option key={day.value} value={day.value}>
                    {day.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Time Range - Moved up for better UX */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Start Time *
              </label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                End Time *
              </label>
              <input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
          </div>

          {/* Time Validation Error */}
          {startTime && endTime && startTime >= endTime && (
            <div className="flex items-center gap-2 p-3 bg-red-900/20 border border-red-500 rounded-lg">
              <AlertTriangle size={20} className="text-red-400" />
              <span className="text-red-400 text-sm">End time must be after start time</span>
            </div>
          )}

          {/* Overlap Warning */}
          {hasOverlaps && isValidTimeRange && (
            <div className="flex items-center gap-2 p-3 bg-red-900/20 border border-red-500 rounded-lg">
              <AlertTriangle size={20} className="text-red-400" />
              <span className="text-red-400 text-sm font-medium">
                Time slot overlaps with existing hours (marked in red below)
              </span>
            </div>
          )}

          {/* Lane Types Selection with Existing Slots */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-3">
              Select Lane Types *
            </label>
            <div className="space-y-2 bg-gray-750 rounded-lg p-4 max-h-64 overflow-y-auto">
              {checkpointLaneTypes.length === 0 ? (
                <p className="text-gray-500 text-sm">No lane types available for this checkpoint</p>
              ) : (
                checkpointLaneTypes.map(laneType => {
                  const existingSlots = getExistingSlotsForLaneType(laneType.lane_type_id);
                  const isChecked = selectedLaneTypeIds.includes(laneType.lane_type_id);
                  
                  return (
                    <label
                      key={laneType.lane_type_id}
                      className={`flex items-start gap-3 p-3 rounded cursor-pointer transition-colors ${
                        isChecked ? 'bg-blue-900/20 border-2 border-blue-500' : 'hover:bg-gray-700 border-2 border-transparent'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleLaneType(laneType.lane_type_id)}
                        className="w-4 h-4 mt-1 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="text-white font-medium">{laneType.type_name}</span>
                          {isChecked && (
                            <span className="text-xs text-blue-400 font-medium">Selected</span>
                          )}
                        </div>
                        {existingSlots.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {existingSlots.map((slot, idx) => {
                              const isOverlapping = isSlotOverlapping(laneType.lane_type_id, slot.id);
                              return (
                                <div 
                                  key={idx}
                                  className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                                    isOverlapping 
                                      ? 'bg-red-900/50 border border-red-500 text-red-300 animate-pulse' 
                                      : 'bg-gray-800 text-gray-300'
                                  }`}
                                >
                                  <Clock size={12} className="opacity-60" />
                                  <span>{slot.start} - {slot.end}</span>
                                  {isOverlapping && (
                                    <AlertTriangle size={12} className="text-red-400 ml-1" />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </label>
                  );
                })
              )}
            </div>
            {selectedLaneTypeIds.length > 0 && (
              <p className="text-sm text-gray-400 mt-2">
                Selected: {selectedLaneTypeIds.length} lane type{selectedLaneTypeIds.length > 1 ? 's' : ''}
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit || saving}
              className={`px-6 py-2 rounded-lg transition-colors ${
                canSubmit && !saving
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-gray-700 text-gray-500 cursor-not-allowed'
              }`}
            >
              {saving ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
